import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Chip,
    TextField,
    MenuItem,
    IconButton,
    Tooltip,
    Divider,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Avatar,
    Stack,
    Button,
    CircularProgress,
    Tabs,
    Tab,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    FilterList as FilterListIcon,
    Search as SearchIcon,
    Launch as LaunchIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import toast from 'react-hot-toast';
import { ordersAPI, adminAPI } from '../../services/api';

dayjs.extend(relativeTime);

const statusColor = (status) => {
    switch (status) {
        case 'paid':
        case 'completed':
        case 'delivered':
            return 'success';
        case 'processing':
        case 'confirmed':
            return 'primary';
        case 'pending':
            return 'warning';
        case 'failed':
        case 'cancelled':
        case 'refunded':
            return 'error';
        default:
            return 'default';
    }
};

const SuperAdminOrdersPage = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all'); // all | byWebsite
    const [orders, setOrders] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalOrders: 0 });
    const [limit, setLimit] = useState(10);
    // Websites tab state
    const [websitesLoading, setWebsitesLoading] = useState(false);
    const [websites, setWebsites] = useState([]);
    const [webPagination, setWebPagination] = useState({ currentPage: 1, totalPages: 1, totalWebsites: 0 });
    const [webLimit, setWebLimit] = useState(10);
    const [filters, setFilters] = useState({
        q: '',
        status: '',
        paymentStatus: '',
        dateRange: '', // today | thisWeek | thisMonth | lastMonth
    });
    const [analytics, setAnalytics] = useState(null);

    const fetchAnalytics = async () => {
        try {
            const tzOffsetMinutes = new Date().getTimezoneOffset();
            const res = await adminAPI.getOrdersAnalytics({ tzOffsetMinutes });
            setAnalytics(res.data);
        } catch (e) {
            // optional
        }
    };

    const fetchOrders = async (page = 1, currentLimit = limit) => {
        try {
            setLoading(true);
            const tzOffsetMinutes = new Date().getTimezoneOffset();
            const res = await ordersAPI.getOrders({ page, limit: currentLimit, tzOffsetMinutes, ...filters });
            setOrders(res.data.orders || []);
            setPagination(res.data.pagination || { currentPage: page, totalPages: 1, totalOrders: 0 });
        } catch (e) {
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    const fetchWebsites = async (page = 1, currentLimit = webLimit) => {
        try {
            setWebsitesLoading(true);
            const tzOffsetMinutes = new Date().getTimezoneOffset();
            const res = await adminAPI.getOrdersByWebsite({ page, limit: currentLimit, tzOffsetMinutes, sort: 'today' });
            setWebsites(res.data.websites || []);
            setWebPagination(res.data.pagination || { currentPage: page, totalPages: 1, totalWebsites: 0 });
        } catch (e) {
            toast.error('Failed to load websites');
        } finally {
            setWebsitesLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders(1, limit);
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeTab === 'byWebsite') {
            fetchWebsites(webPagination.currentPage || 1, webLimit);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleChangePage = (_e, newPage) => {
        fetchOrders(newPage + 1, limit);
    };

    const handleChangeRowsPerPage = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setLimit(newLimit);
        fetchOrders(1, newLimit);
    };

    const handleWebPageChange = (_e, newPage) => {
        fetchWebsites(newPage + 1, webLimit);
    };

    const handleWebRowsPerPage = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setWebLimit(newLimit);
        fetchWebsites(1, newLimit);
    };

    const applyFilters = () => {
        fetchOrders(1, limit);
    };

    const clearFilters = () => {
        setFilters({ q: '', status: '', paymentStatus: '', dateRange: '' });
        fetchOrders(1, limit);
    };

    const totalsBy = useMemo(() => {
        const daily = {};
        const weekly = {};
        const monthly = {};
        const byWebsite = {};

        orders.forEach((o) => {
            const dayKey = dayjs(o.createdAt).format('YYYY-MM-DD');
            const weekKey = dayjs(o.createdAt).format('YYYY-[W]WW');
            const monthKey = dayjs(o.createdAt).format('YYYY-MM');

            daily[dayKey] = (daily[dayKey] || 0) + 1;
            weekly[weekKey] = (weekly[weekKey] || 0) + 1;
            monthly[monthKey] = (monthly[monthKey] || 0) + 1;

            if (o.website) {
                const wKey = o.website._id;
                byWebsite[wKey] = byWebsite[wKey] || { name: o.website.name, count: 0, amount: 0 };
                byWebsite[wKey].count += 1;
                byWebsite[wKey].amount += Number(o.totalAmount || 0);
            }
        });

        return { daily, weekly, monthly, byWebsite };
    }, [orders]);

    return (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                        <CardHeader
                            title={
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="h5">Orders</Typography>
                                    <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} aria-label="orders tabs">
                                        <Tab value="all" label="All Orders" />
                                        <Tab value="byWebsite" label="Orders By Website" />
                                    </Tabs>
                                </Stack>
                            }
                            action={
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title="Refresh">
                                        <IconButton onClick={() => { activeTab === 'all' ? fetchOrders(pagination.currentPage, limit) : fetchWebsites(webPagination.currentPage, webLimit); fetchAnalytics(); }}>
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            }
                        />
                        <CardContent>
                            {activeTab === 'all' ? (
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Search"
                                            placeholder="Order #, Customer, Email, Website"
                                            value={filters.q}
                                            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                                            InputProps={{ endAdornment: <SearchIcon fontSize="small" /> }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            select
                                            label="Date Range"
                                            value={filters.dateRange}
                                            onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            <MenuItem value="today">Today</MenuItem>
                                            <MenuItem value="thisWeek">This Week</MenuItem>
                                            <MenuItem value="thisMonth">This Month</MenuItem>
                                            <MenuItem value="lastMonth">Last Month</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            select
                                            label="Order Status"
                                            value={filters.status}
                                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            {['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded', 'failed'].map(s => (
                                                <MenuItem key={s} value={s}>{s}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            select
                                            label="Payment Status"
                                            value={filters.paymentStatus}
                                            onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            {['pending', 'paid', 'failed', 'refunded'].map(s => (
                                                <MenuItem key={s} value={s}>{s}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <Stack direction="row" spacing={1}>
                                            <Button variant="contained" onClick={applyFilters} startIcon={<FilterListIcon />}>Apply</Button>
                                            <Button variant="text" onClick={clearFilters}>Clear</Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Websites sorted by highest Today orders.</Typography>
                                    </Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Analytics quick stats */}
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        {[{ key: 'today', label: 'Today Orders', value: analytics?.totals?.today ?? 0 },
                        { key: 'thisWeek', label: 'This Week Orders', value: analytics?.totals?.week ?? 0 },
                        { key: 'thisMonth', label: 'This Month Orders', value: analytics?.totals?.month ?? 0 },
                        { key: 'lastMonth', label: 'Last Month Orders', value: analytics?.totals?.lastMonth ?? 0 }].map((item) => (
                            <Grid item xs={12} md={3} key={item.key}>
                                <Card sx={{ borderRadius: 3 }}>
                                    <CardContent>
                                        <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                                            <Typography variant="subtitle1" fontWeight={600}>{item.label}</Typography>
                                            <Button size="small" onClick={() => { setFilters(f => ({ ...f, dateRange: item.key })); fetchOrders(1, limit); }}>Filter</Button>
                                        </Stack>
                                        <Typography variant="h3" fontWeight={800}> {item.value} </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                {/* Content tables */}
                {activeTab === 'all' ? (
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: 3 }}>
                            <CardContent>
                                {loading ? (
                                    <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
                                ) : (
                                    <>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Order #</TableCell>
                                                    <TableCell>Customer</TableCell>
                                                    <TableCell>Website</TableCell>
                                                    <TableCell align="right">Total</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Payment</TableCell>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell align="center">Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {orders.map((o) => (
                                                    <TableRow key={o._id} hover>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.light' }}>{(o.orderNumber || 'O')?.slice(-2)}</Avatar>
                                                                <Box>
                                                                    <Typography variant="body2" fontWeight={600}>{o.orderNumber}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">{o._id}</Typography>
                                                                </Box>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight={600}>{o.customerName || '-'}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{o.customerEmail || '-'}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">{o.website?.name || '-'}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{o.website?.siteId || '-'}</Typography>
                                                        </TableCell>
                                                        <TableCell align="right">${Number(o.totalAmount || 0).toFixed(2)}</TableCell>
                                                        <TableCell>
                                                            <Chip label={o.status} color={statusColor(o.status)} size="small" />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={o.paymentStatus} color={statusColor(o.paymentStatus)} size="small" variant="outlined" />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">{dayjs(o.createdAt).format('YYYY-MM-DD HH:mm')}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{dayjs(o.createdAt).fromNow()}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Tooltip title="Open">
                                                                <IconButton size="small" onClick={() => window.open(`/dashboard/orders?focus=${o._id}`, '_blank')}>
                                                                    <LaunchIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {orders.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={8} align="center">
                                                            <Typography variant="body2" color="text.secondary">No orders found</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                        <TablePagination
                                            component="div"
                                            count={pagination.totalOrders || 0}
                                            page={(pagination.currentPage || 1) - 1}
                                            onPageChange={handleChangePage}
                                            rowsPerPage={limit}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            rowsPerPageOptions={[10, 25, 50, 100]}
                                        />
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ) : (
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: 3 }}>
                            <CardContent>
                                {websitesLoading ? (
                                    <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
                                ) : (
                                    <>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Website</TableCell>
                                                    <TableCell>Site ID</TableCell>
                                                    <TableCell align="right">Today</TableCell>
                                                    <TableCell align="right">This Week</TableCell>
                                                    <TableCell align="right">This Month</TableCell>
                                                    <TableCell align="right">Last Month</TableCell>
                                                    <TableCell align="right">Total</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {websites.map((w) => (
                                                    <TableRow key={w.websiteId} hover>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight={600}>{w.name}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">{w.siteId}</Typography>
                                                        </TableCell>
                                                        <TableCell align="right">{w.today}</TableCell>
                                                        <TableCell align="right">{w.week}</TableCell>
                                                        <TableCell align="right">{w.month}</TableCell>
                                                        <TableCell align="right">{w.lastMonth}</TableCell>
                                                        <TableCell align="right">{w.total}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {websites.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={7} align="center">
                                                            <Typography variant="body2" color="text.secondary">No websites found</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                        <TablePagination
                                            component="div"
                                            count={webPagination.totalWebsites || 0}
                                            page={(webPagination.currentPage || 1) - 1}
                                            onPageChange={handleWebPageChange}
                                            rowsPerPage={webLimit}
                                            onRowsPerPageChange={handleWebRowsPerPage}
                                            rowsPerPageOptions={[10, 25, 50, 100]}
                                        />
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Aggregations by website (current page) */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardHeader title={<Typography variant="h6">By Website (current page)</Typography>} />
                        <CardContent>
                            {Object.keys(totalsBy.byWebsite).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No data</Typography>
                            ) : (
                                Object.values(totalsBy.byWebsite).map((w) => (
                                    <Box key={w.name} display="flex" justifyContent="space-between" py={1}>
                                        <Typography>{w.name}</Typography>
                                        <Typography fontWeight={600}>{w.count} • ${w.amount.toFixed(2)}</Typography>
                                    </Box>
                                ))
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                {/* Global top websites/users from analytics */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardHeader title={<Typography variant="h6">Top Websites (all)</Typography>} />
                        <CardContent>
                            {!analytics?.byWebsite?.length ? (
                                <Typography variant="body2" color="text.secondary">No data</Typography>
                            ) : (
                                analytics.byWebsite.map((w) => (
                                    <Box key={w.websiteId} display="flex" justifyContent="space-between" py={1}>
                                        <Typography>{w.name}</Typography>
                                        <Typography fontWeight={600}>{w.count} • ${Number(w.amount || 0).toFixed(2)}</Typography>
                                    </Box>
                                ))
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardHeader title={<Typography variant="h6">Top Users (all)</Typography>} />
                        <CardContent>
                            {!analytics?.byUser?.length ? (
                                <Typography variant="body2" color="text.secondary">No data</Typography>
                            ) : (
                                analytics.byUser.map((u) => (
                                    <Box key={u._id} display="flex" justifyContent="space-between" py={1}>
                                        <Typography>User {u._id}</Typography>
                                        <Typography fontWeight={600}>{u.count} • ${Number(u.amount || 0).toFixed(2)}</Typography>
                                    </Box>
                                ))
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box >
    );
};

export default SuperAdminOrdersPage;


